import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

export type InputTone = "info";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Render `border-destructive ring-destructive` to indicate validation error. */
  error?: boolean;
  /** When `type="password"`, show an eye toggle on the right to reveal the value. */
  revealable?: boolean;
  /** Optional semantic tone — adjusts border/focus tint. Currently only "info". */
  tone?: InputTone;
  /** When `false`, BASE drops `w-full` so the input can be sized by parent flex. Default `true`. */
  block?: boolean;
}

const BASE_NO_WIDTH =
  "rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground disabled:bg-muted disabled:cursor-not-allowed";
const BLOCK = "w-full";
const TONE_INFO = "border-info-soft focus:border-info focus:ring-info";
const ERROR_CLASSES = "border-destructive focus:border-destructive focus:ring-destructive";

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input(
    { error, revealable, tone, block = true, type = "text", className, ...rest },
    ref,
  ) {
    const [revealed, setRevealed] = React.useState(false);
    const isPasswordReveal = revealable && type === "password";
    const effectiveType = isPasswordReveal && revealed ? "text" : type;

    const inputClasses = [
      BASE_NO_WIDTH,
      block ? BLOCK : "",
      tone === "info" ? TONE_INFO : "",
      error ? ERROR_CLASSES : "",
      isPasswordReveal ? "pr-9" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    if (!isPasswordReveal) {
      return (
        <input
          ref={ref}
          type={effectiveType}
          className={inputClasses}
          {...rest}
        />
      );
    }

    return (
      <div className={block ? "relative w-full" : "relative"}>
        <input
          ref={ref}
          type={effectiveType}
          className={inputClasses}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          tabIndex={-1}
          aria-label={revealed ? "Hide value" : "Reveal value"}
          className="absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground hover:text-foreground"
        >
          {revealed ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
    );
  },
);
